import React, { useEffect, useMemo } from 'react';
import { Card, Typography, Box } from '@mui/material';
import { useDashboardData } from '../../../contexts/DashboardDataContext';
import { useDashboardContext } from '../../Dashboard';
import { useNavigate } from 'react-router-dom';

const EstimateStatusWidget = () => {
    const { estimateData, fetchEstimateData, getCachedData } = useDashboardData();
    // Remove unused currentZone
    // const { currentZone } = useDashboardContext();
    const { loading, data, error } = estimateData;
    const navigate = useNavigate();

    // Get cached data if available
    const cachedData = useMemo(() => {
        return getCachedData?.('estimateData') || null;
    }, [getCachedData]);

    const getStatusName = (key) => {
        const statusNames = {
            estsent: 'EstSent',
            estok: 'EstOk',
            estreturn: 'EstReturn',
            estscrap: 'EstScrap'
        };
        return statusNames[key] || key;
    };

    const getStatusColor = (days) => {
        if (days < 3) return '#4caf50'; // Green
        if (days < 5) return '#ff9800'; // Orange
        return '#ff0000'; // Red
    };

    // Helper to handle click on a status category
    const handleCategoryClick = (statusKey) => {
        // Map to correct status string for search
        const statusMap = {
            estsent: 'EstSent',
            estok: 'EstOk',
            estreturn: 'EstReturn',
            estscrap: 'EstScrap'
        };
        const statusValue = statusMap[statusKey] || statusKey;
        // Navigate to activities with search param
        navigate(`/activities?tab=queues&subTab=open&search=status=${encodeURIComponent(statusValue)}`);
    };

    // Add effect to fetch data only if not already available
    useEffect(() => {
        // If we have neither data nor cached data, fetch it
        if (!data && !cachedData && !loading && !error) {
            console.log('[EstimateStatusWidget] No data available, triggering fetch');
            fetchEstimateData(true);
        }
    }, [data, cachedData, loading, error, fetchEstimateData]);

    // If we have data OR cached data, show it
    const displayData = data || cachedData;
    if (displayData) {
        return (
            <Card
                sx={{
                    padding: 2,
                    backgroundColor: '#1e1e1e',
                    color: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    width: '600px',
                    height: '165px',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative'
                }}
            >
                <h3>Estimate Status</h3>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mx: 'auto',
                    width: '100%',
                    flex: 1
                }}>
                    {['estsent', 'estok', 'estreturn', 'estscrap'].map((key) => {
                        const statusData = displayData && displayData[key] ? displayData[key] : { count: 0, oldestDays: 0 };
                        const color = getStatusColor(statusData.oldestDays);
                        return (
                            <Box 
                                key={key} 
                                sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: '25%',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' }
                                }}
                                onClick={() => handleCategoryClick(key)}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '2.5rem',
                                        color: color,
                                        mb: 0.5
                                    }}
                                >
                                    {statusData.count}
                                </Typography>
                                <Typography 
                                    variant="caption"
                                    sx={{ 
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    {getStatusName(key)}
                                </Typography>
                                {statusData.count > 0 && (
                                    <Typography 
                                        variant="caption"
                                        sx={{ 
                                            fontSize: '0.75rem',
                                            color: color,
                                            opacity: 0.8
                                        }}
                                    >
                                        ({statusData.oldestDays.toFixed(1)} days)
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                
                {/* Show refreshing indicator when loading with existing data */}
                {loading && (
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            position: 'absolute',
                            bottom: 5,
                            right: 10,
                            color: 'gray'
                        }}
                    >
                        Refreshing...
                    </Typography>
                )}
            </Card>
        );
    }

    // Only show loading state when we have no data AND no cached data
    if (loading && !displayData) {
        return (
            <Card
                sx={{
                    padding: 3,
                    backgroundColor: '#1e1e1e',
                    color: '#fff',
                    borderRadius: '8px',
                    textAlign: 'center',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    width: '600px',
                    height: '165px',
                    margin: '0 auto'
                }}
            >
                <Typography variant="h5" gutterBottom>
                    Estimate Status
                </Typography>
                <Typography variant="body1">Loading...</Typography>
            </Card>
        );
    }

    if (error && !displayData) {
        return (
            <Card
                sx={{
                    padding: 3,
                    backgroundColor: '#1e1e1e',
                    color: '#fff',
                    borderRadius: '8px',
                    textAlign: 'center',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    width: '600px',
                    height: '165px',
                    margin: '0 auto'
                }}
            >
                <Typography variant="h5" gutterBottom>
                    Estimate Status
                </Typography>
                <Typography variant="body1">Error loading data</Typography>
                <button onClick={fetchEstimateData} style={{ marginTop: 10 }}>Refresh Data</button>
            </Card>
        );
    }

    // Last resort - should rarely happen
    return (
        <Card
            sx={{
                padding: 3,
                backgroundColor: '#1e1e1e',
                color: '#fff',
                borderRadius: '8px',
                textAlign: 'center',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                width: '600px',
                height: '165px',
                margin: '0 auto'
            }}
        >
            <Typography variant="h5" gutterBottom>
                Estimate Status
            </Typography>
            <Typography variant="body1">Waiting for data...</Typography>
            <button onClick={() => fetchEstimateData(true)} style={{ marginTop: 10 }}>Fetch Data</button>
        </Card>
    );
};

export default EstimateStatusWidget;
